import React, { useState, useRef, useContext, useMemo, useEffect, useCallback } from "react";
import classNames from "classnames";
import styles from "./AccordionCard.module.scss";
import { UnmountClosed } from 'react-collapse';
import { TransitionChevron, Span, ThreeDotLoader, Button, CheckComponent, Tooltip, Popover } from "./../../";
import uuidv4 from "uuid/v4";
import ComponentRenderError from "./ComponentRenderError";
import _ from "lodash";

const AccordionContext = React.createContext(null);

export const useAccordionContext = () => {
    return useContext(AccordionContext);
}

export const AccordionController = React.memo(({ children }) => {
    const [openChildren, setOpenChildren] = useState(null);
    const idRef = useRef(uuidv4());

    const toggleOpenChild = useCallback((childId) => {
        return setOpenChildren(prev => {
            if (prev?.[childId]) {
                return {
                    ...prev,
                    [childId]: false
                }
            } else {
                return {
                    [childId]: true
                }
            }
        })
    }, [setOpenChildren]);

    const controller = useMemo(() => {
        return {
            openChildren,
            toggleOpenChild,
            setOpenChildren,
            id: idRef.current,
            closeAll: () => {
                setOpenChildren(null)
            }
        }
    }, [openChildren, toggleOpenChild, idRef, setOpenChildren]);

    return <AccordionContext.Provider value={controller}>
        {children}
    </AccordionContext.Provider>
});

export const AccordionTable = React.memo(({ children, isGrouped = false }) => {
    const context = useAccordionContext();
    const ref = useRef();

    useEffect(() => {
        const accordionTableHeaderStyle = window.document.createElement('style');
        accordionTableHeaderStyle.type = 'text/css';

        window.document.head.appendChild(accordionTableHeaderStyle);

        if (context?.id) {
            let max = 0;
            for (const card of ref.current.getElementsByClassName(`at-${context.id}`)) {
                max = Math.max(card.clientWidth, max);
            }
            accordionTableHeaderStyle.innerHTML = `.${styles.header}.ah-${context.id} {
                grid-template-columns: minmax(${max}px, max-content) 1fr max-content max-content max-content;
            }`;
        }

        return () => {
            window.document.head.removeChild(accordionTableHeaderStyle);
        };
    }, [context?.id]);

    if (!context) {
        return <ComponentRenderError>Accordion Table Rendered outside AccordionController!</ComponentRenderError>
    }

    return <div ref={ref} className={classNames(styles['accordion-table'], { [styles['is-grouped']]: isGrouped })}>
        {children}
    </div>
});

export const AccordionCardContent = React.memo(({ children, className, style }) => {
    return <div className={classNames(styles['accordion-card-content'], className)} style={style}>
        {children}
    </div>
});

export const AccordionCardTitle = React.memo(({children, className, style}) => {
    return <div className={classNames(className)} style={style}>{children}</div>
});


const AccordionValue = React.memo(({ value, ValueContent, isDisabled }) => {
    if (_.isArray(value)) {
        const values = value.filter(value => !!value)
        return <div className={styles['multi-value']}>
            {values.map((value, index) => {
                return <Span color={isDisabled ? "text-disabled" : undefined} key={index}>{value}</Span>
            })}
            <Tooltip>{values.join(", ")}</Tooltip>
        </div>
    } else if (ValueContent) {
        return <ValueContent isDisabled={isDisabled}/>
    } else {
        return <Span color={isDisabled ? "text-disabled" : undefined}>{value}</Span>
    }
})

/**
 * isDisabled affects value/title appearance and prevents selection but does not prevent expand
 * isExpandDisabled prevents only expand
 * isLoading sets both isDisabled and isExpandDisabled
 *
 * If an AccordionCard can be expanded, and it doesnt have select option or left/right widgets, then click on the header will expand
 * If the card is selectable or has left/right widgets, only click on arrow will expand
 */
const AccordionCard = React.memo(({
                                      title,
                                      value,
                                      ValueContent,
                                      isLoading,
                                      LeftWidget,
                                      RightWidget,
                                      children,
                                      className,
                                      style,
                                      headerClassName,
                                      headerStyle,
                                      isDisabled: _isDisabled,
                                      isExpandDisabled: _isExpandDisabled,
                                      isSelected,
                                      onSelect,
                                      name,
                                      tooltip
                                  }) => {
    const context = useAccordionContext();
    if (!context) {
        return <ComponentRenderError>Accordion Card Rendered outside AccordionController!</ComponentRenderError>
    }
    const { openChildren, toggleOpenChild } = context;
    let titleChild;
    let restChild;
    if (!_.isUndefined(children) && _.isArray(children)) {
        titleChild = _.filter(children, (child) => child?.type === AccordionCardTitle)[0];
        restChild = _.reject(children, (child) => child?.type === AccordionCardTitle);
    } else if (!_.isUndefined(children) && !_.isArray(children)) {
        if (children.type === AccordionCardTitle) {
            titleChild = children;
        } else  {
            restChild = children
        }
    };

    const isExpandDisabled = _isExpandDisabled || isLoading;
    const isDisabled = _isDisabled || isLoading;
    const id = useRef(name || uuidv4());
    const isOpen = openChildren?.[id.current];
    const canOpen = !!restChild && !isExpandDisabled;
    const hasInnerContent = !!restChild;
    const hasInnerControls = LeftWidget || RightWidget || onSelect;
    const showValue = (value || ValueContent) && !isOpen && !isLoading;

    const HeaderComponent = canOpen && !hasInnerControls ? "button" : "div";

    return <div className={classNames(styles.base, className)} style={style}>
        <HeaderComponent className={classNames(styles.header, headerClassName, `ah-${context.id}`, {
            [styles['hoverable']]: !isOpen && !isDisabled,
            [styles['clickable']]: canOpen && !hasInnerControls,
            [styles['disabled']]: isDisabled
        })}
                         style={headerStyle}
                         onClick={canOpen && !hasInnerControls ? () => toggleOpenChild(id.current) : undefined}>
            <div className={`${styles['title']} at-${context.id}`}>
                {onSelect && <Button type="none" onClick={onSelect} disabled={isDisabled}>
                    <CheckComponent isSelected={isSelected} isHoverable={true} isDisabled={isDisabled}/>
                </Button>}
                {tooltip && <Popover triggerClassName={styles["title-popover"]}>{tooltip}</Popover>}
                {<Span bold color={isDisabled ? "text-disabled" : undefined}>{titleChild ? titleChild : title}</Span>}
            </div>
            {showValue && <AccordionValue value={value} ValueContent={ValueContent} isDisabled={isDisabled}/>}
            {!showValue && !isLoading && <div/>}
            {isLoading && <div className={styles.loader}><ThreeDotLoader/><Span color="text-disabled">Loading Data</Span></div>}
            {LeftWidget ? <LeftWidget/> : <div/>}
            {RightWidget ? <RightWidget/> : <div/>}
            {hasInnerContent && !hasInnerControls && <TransitionChevron isActive={isOpen} wide color="icon-secondary"/>}
            {hasInnerContent && hasInnerControls && <Button type="none" disabled={!canOpen} onClick={() => toggleOpenChild(id.current)}><TransitionChevron isActive={isOpen} wide color="icon-secondary"/></Button>}
            {!hasInnerContent && <div/>}
        </HeaderComponent>
        <UnmountClosed isOpened={isOpen} theme={{ collapse: styles['animation'] }}>
            {restChild}
        </UnmountClosed>
    </div>
});

export default AccordionCard;